import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DatePicker as AntdDatePicker } from 'antd';
import moment from 'moment';
import { CloseCircleOutlined } from '@ant-design/icons';
import Icon, { Calendar } from 'components/Atoms/Icon';
import cn from 'classnames';

import { getTimeFormat } from 'utilities/time';
import './styles.scss';
import { isEmpty } from 'lodash';
import { FormattedMessage } from "react-intl";

const { RangePicker: AntdRangePicker } = AntdDatePicker;

const RangePicker = ({
  label = 'Label',
  value,
  allowClear,
  type,
  onlyPicker = false,
  className = '',
  renderExtraFooter,
  ...restProps
}) => {
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [datePickerValue, setDatePickerValue] = useState(null);

  const toggleShowDatePicker = (visible) => () => {
    setShowDatePicker(visible);
  };

  const onChange = (dates) => {
    setShowDatePicker(false);
    setDatePickerValue(dates);
    restProps.onChange && restProps.onChange(dates);
  };

  const onClearValue = () => {
    setDatePickerValue([]);
    restProps?.onChange([]);
  };

  const displayValue = useMemo(() => {
    if (isEmpty(datePickerValue)) return 'All';

    return Array.isArray(datePickerValue, restProps.format)
      ? `${getTimeFormat(
          datePickerValue[0],
          restProps.format
        )} → ${getTimeFormat(datePickerValue[1], restProps.format)} `
      : getTimeFormat(datePickerValue);
  }, [datePickerValue]);

  const generateExtraFooter = useCallback(() => {
    const currentDate = moment();
    const listExtraDate = [
      {
        value: [currentDate, currentDate],
        name: <FormattedMessage id="date_picker.today" />,
      },
      {
        value: [currentDate.clone().subtract(1, 'months'), currentDate],
        name: <FormattedMessage id="date_picker.last_month" />,
      },
      {
        value: [currentDate.clone().subtract(2, 'months'), currentDate],
        name: <FormattedMessage id="date_picker.last_60_days" />,
      },
      {
        value: [currentDate.clone().subtract(3, 'months'), currentDate],
        name: <FormattedMessage id="date_picker.last_90_days" />,
      },
    ];
    return listExtraDate.map((e, i) => (
      <span
        className="extra-item ml-10"
        key={`${e.name}-${i}`}
        onClick={() => onChange(e.value)}
      >
        {e.name}
      </span>
    ));
  });

  const suffixIconCustom = useMemo(() => {
    if (!isEmpty(datePickerValue) && allowClear) {
      return (
        <Icon
          className="icon-clear-custom"
          component={CloseCircleOutlined}
          onClick={onClearValue}
        />
      );
    }
    return <Icon component={Calendar} />;
  }, [datePickerValue, allowClear]);

  const onOpenChange = (open) => {
    setShowDatePicker(open);
  };

  useEffect(() => {
    if (!isEmpty(value)) setDatePickerValue(value);
  }, [value]);

  return (
    <div className={cn('custom-picker', className)}>
      {!showDatePicker && !onlyPicker ? (
        <div className="select-date-picker">
          <div className="wrapper" onClick={toggleShowDatePicker(true)}>
            <span className="label">{label}:</span>
            <span className="value">{displayValue}</span>
          </div>
          <div className="suffix-icon">{suffixIconCustom}</div>
        </div>
      ) : (
        <AntdRangePicker
          {...restProps}
          renderExtraFooter={renderExtraFooter || generateExtraFooter}
          showToday={false}
          allowClear={false}
          suffixIcon={<Icon component={Calendar} />}
          open={showDatePicker}
          onChange={onChange}
          onOpenChange={onOpenChange}
          format={restProps.format}
          value={datePickerValue}
          dropdownClassName="panel-container"
        />
      )}
    </div>
  );
};

export default RangePicker;
